import { ArcRotateCamera, Observer, Engine } from '@babylonjs/core';

interface PointerEventInit {
	pointerId: number;
	screenX: number;
	screenY: number;
	clientX: number;
	clientY: number;
}
class PointerEvent {
	public type: string;
	public pointerId: number;
	public screenX: number;
	public clientX: number;
	public x: number;
	public y: number;
	public srcElement: HTMLElement | null;
	public target: HTMLElement | null;
	public preventDefault: () => void;
	constructor(type: string, init: PointerEventInit) {
		this.type = type;
		this.pointerId = init.pointerId || 0;
		this.screenX = init.screenX || 0;
		this.screenX = init.screenY || 0;
		this.clientX = init.clientX || 0;
		this.clientX = init.clientY || 0;
		this.x = 0;
		this.y = 0;
		this.srcElement = null;
		this.target = null;
		this.preventDefault = function (): void {
			return;
		};
	}
}
window.PointerEvent = PointerEvent;

export class ArcRotateCameraTouchInput {
	public angularSensibilityX: number;
	public angularSensibilityY: number;
	public camera: ArcRotateCamera;
	private touchStart: (evt: TouchEvent) => void;
	private touchMove: (evt: TouchEvent) => void;
	private touchEnd: (evt: TouchEvent) => void;
	private resizeObserver: Observer<Engine> | null;
	private clientRect: ClientRect;

	constructor() {
		this.angularSensibilityX = 1000.0;
		this.angularSensibilityY = 1000.0;
	}

	public getTypeName(): string {
		return 'ArcRotateCameraTouchInput';
	}

	public getSimpleName(): string {
		return 'touch';
	}

	public getClassName(): string {
		return 'ArcRotateCameraTouchInput';
	}

	public attachControl(): void {
		const scene = this.camera.getScene();
		const engine = this.camera.getEngine();
		if (!this.resizeObserver) {
			this.resizeObserver = engine.onResizeObservable.add(() => {
				const client = engine.getRenderingCanvasClientRect();
				if (client) {
					this.clientRect = client;
				}
			});
		}

		let previousX = 0;
		let previousY = 0;
		let pointer: Touch | null = null;
		this.touchStart = (evt: TouchEvent): void => {
			previousX = evt.touches[0].clientX;
			previousY = evt.touches[0].clientY;
			const pick = scene.pick(evt.touches[0].clientX, evt.touches[0].clientY);
			if (pick) {
				scene.simulatePointerDown(pick, {
					pointerId: evt.touches[0].identifier,
				});
			}
		};

		this.touchMove = (evt: TouchEvent): void => {
			pointer = evt.touches[0];
			const rect = this.getCanvasClientRect();
			const half = rect.bottom / 2;
			const modifier = pointer.clientY > half ? 1 : -1;
			const x = pointer.clientX - previousX;
			const y = pointer.clientY - previousY;
			this.camera.inertialAlphaOffset -= (x * modifier) / this.angularSensibilityX;
			this.camera.inertialBetaOffset -= y / this.angularSensibilityY;

			previousX = pointer.clientX;
			previousY = pointer.clientY;
			const pick = scene.pick(evt.touches[0].clientX, evt.touches[0].clientY);
			if (pick) {
				scene.simulatePointerMove(pick, {
					pointerId: pointer.identifier,
				});
			}
		};

		this.touchEnd = (evt: TouchEvent): void => {
			const lastTouch = evt.changedTouches[0];
			previousX = 0;
			previousY = 0;
			const pick = scene.pick(lastTouch.clientX, lastTouch.clientY);
			if (pick) {
				scene.simulatePointerUp(pick, { pointerId: lastTouch.identifier });
			}
		};

		const element = document.getElementsByTagName('kompis-view-3d')[0];
		if (element) {
			element.addEventListener('touchstart', this.touchStart, false);
			element.addEventListener('touchmove', this.touchMove, false);
			element.addEventListener('touchend', this.touchEnd, false);
		}
	}

	public detachControl(): void {
		const element = document.getElementsByTagName('kompis-view-3d')[0];
		if (element) {
			element.removeEventListener('touchstart', this.touchStart);
			element.removeEventListener('touchmove', this.touchMove);
			element.removeEventListener('touchend', this.touchEnd);
		}
	}

	private getCanvasClientRect(): ClientRect {
		if (this.clientRect) {
			return this.clientRect;
		}
		const client = this.camera.getEngine().getRenderingCanvasClientRect();
		if (client) {
			this.clientRect = client;
			return this.clientRect;
		}

		throw 'ArcRotateCameraTouchInput: getCanvasClientRect(): client does not exist.';
	}
}
